// Week 8 / Day 2 — Риск, неопределённость и целые числа / Risk, Uncertainty and Signed Numbers
// Solid Grade 6 math + RU Grade 3–4. Educational model; not financial advice.

export const maxWeek8Day2 = {
  id: 'max-week-8-day-2',
  studentId: 'max',
  weekId: 'week-8',
  dayId: 'day-2',
  title: 'Риск, неопределённость и целые числа / Risk, Uncertainty and Signed Numbers',
  shortTitle: 'Риск и целые числа / Risk and Signed Numbers',
  theme:
    'Макс классифицирует риск, работает с процентами и положительными/отрицательными изменениями. / Max classifies risk and works with percent and signed changes.',
  estimatedTimeLabel: 'Онлайн: 25–35 мин. Полный день: 80–100 мин. / Online: 25–35 min. Full day: 80–100 min.',
  nimnyIntro:
    'Нимни ведёт Макса через категории риска, отклонения со знаками, риск по выборке и уравнение проверки. / Nimny walks Max through risk categories, signed deviations, sample risk and a check equation.',
  goals: [
    'Слой 1. Математика / Layer 1. Math: проценты, знаковые числа, среднее/размах, уравнения со скобками (Grade 6). / percent, signed numbers, mean/range, bracket equations.',
    'Слой 2. Русский / Layer 2. Russian: −тся / −ться, безударные, исправление.',
    'Слой 3. Чтение и письмо / Layer 3. Reading: короткий текст о риске + план + 4 предложения.',
    'Слой 4. Наблюдение и логика / Layer 4. Observation and logic: матрица риска.',
    'Слой 5. Практика: карточка риска для одного решения. / Practical: risk card for one decision.',
    'Дневник мышления / Thinking Diary.',
    'Награда нимами / Reward in nims: 3/5.'
  ],
  vocabulary: [
    { term: 'риск / risk', definition: 'вероятность того, что результат не совпадёт с планом / probability that the result differs from the plan' },
    { term: 'вероятность / probability', definition: 'насколько событие может произойти / how likely an event is' },
    { term: 'последствие / consequence', definition: 'что случится, если событие произошло / what happens if the event occurs' },
    { term: 'решение / decision', definition: 'выбор одного варианта из нескольких / a choice among options' },
    { term: 'проверка / check', definition: 'этап поиска ошибок / step of finding errors' },
    { term: 'отклонение / deviation', definition: 'разница между фактом и целью / actual − goal' }
  ],
  onlineTasks: [
    { id: 'q1', type: 'math', title: 'Категории риска / Risk categories',
      prompt: '120 решений. 15% — высокий риск, 35% — средний, остальные — низкий. Сколько в каждой категории? / Split 120 by 15/35/rest.',
      helper: '0.15 × 120; 0.35 × 120; остаток. / Multiply then subtract.',
      answer: 'Высокий 18; средний 42; низкий 60. / 18 / 42 / 60.', unit: 'решений / decisions' },
    { id: 'q2', type: 'math', title: 'Знаковые изменения / Signed changes',
      prompt: 'Изменения: Пн +12, Вт −8, Ср +5, Чт −11, Пт +7. Общее, среднее, максимум и размах. / Sum, mean, max, range.',
      helper: 'Складывай со знаками; размах = max − min. / Signed sum; range = max − min.',
      answer: 'Сумма +5; среднее +1; максимум +12; размах 23. / +5 / +1 / 12 / 23.', unit: '' },
    { id: 'q3', type: 'math', title: 'Риск по выборке / Sample risk',
      prompt: 'В 45 попытках проблемы 9 раз. Процент? При той же частоте в 150 попытках сколько ожидаем? / Rate + expected count.',
      helper: '9 ÷ 45; затем 20% × 150. / Rate then extrapolate.',
      answer: '20%; ожидается 30 проблем. / 20%; 30 issues.', unit: '%' },
    { id: 'q4', type: 'classify', title: '−тся / −ться',
      prompt: 'Определи форму глагола: пишется «−тся» или «−ться»? / Which form?',
      categories: [
        { id: 'tsa', label: '−тся (что делает?) / -tsa' },
        { id: 'tsya', label: '−ться (что делать?) / -tsya' }
      ],
      items: [
        { id: 't1', text: 'проверять(ся) / to be checked' },
        { id: 't2', text: 'учиться / to learn' },
        { id: 't3', text: 'учит(ся) / learns' },
        { id: 't4', text: 'решает(ся) / gets decided' },
        { id: 't5', text: 'ошибиться / to make a mistake' },
        { id: 't6', text: 'меняет(ся) / changes' }
      ] },
    { id: 'q5', type: 'text', title: 'Исправь ошибки / Fix mistakes',
      prompt: 'Перепиши без ошибок: «риск не означаит абсолютной неудачи, а показываит возможныи последствия». / Rewrite without mistakes.',
      placeholder: 'Риск не означает... / Risk does not mean...' },
    { id: 'q6', type: 'text', title: 'Главная мысль и план / Main idea and plan',
      prompt: 'Прочитай текст «Что такое риск» на печатном листе. Главная мысль, план из 3 пунктов, 4 предложения. / Main idea + plan + 4 sentences.',
      placeholder: 'Главная мысль: ... / Main idea: ...' },
    { id: 'q7', type: 'text', title: 'Матрица риска / Risk matrix',
      prompt: 'Таблица: A (низкая вер., большое последствие); B (высокая вер., маленькое последствие); C (средняя вер., среднее последствие). Какое решение нельзя оценивать только по вероятности, какое — только по последствию, и какие 2 показателя нужны вместе? / Which needs both dimensions?',
      placeholder: 'A — ... B — ... C — ...' },
    { id: 'q8', type: 'text', title: 'Практика: карточка риска / Practical: risk card',
      prompt: 'Возьми одно учебное решение. Оцени вероятность (низкая/средняя/высокая) и последствие (маленькое/среднее/большое). Опиши, что проверить перед решением. / One decision + 3 fields.',
      placeholder: 'Решение: ... Вероятность: ... Последствие: ... Проверка: ...' },
    { id: 'q9', type: 'essay', title: 'Почему нужны оба показателя / Why we need both',
      prompt: '3–5 предложений: почему смотреть только на вероятность или только на последствие недостаточно. / 3–5 sentences.',
      minSentences: 3, maxSentences: 6, placeholder: 'Если смотреть только... / If we only look at...' }
  ],
  notebookInstructions: [
    'Заголовок: «Неделя 8, День 2». / Heading.',
    'Слой 1. Математика: q1–q3 + MATH 4 (уравнение) из печатного листа. / Math + printed equation.',
    'Слой 2. Русский: −тся/−ться + исправление. / Russian.',
    'Слой 3. Чтение: главная мысль + план + 4 предложения. / Reading.',
    'Слой 4. Логика: матрица риска. / Logic.',
    'Слой 5. Практика: карточка риска. / Practical.',
    'Дневник мышления / Thinking diary.',
    'Награда нимами. / Reward.'
  ],
  printWorksheet: {
    title: 'Неделя 8, День 2 / Week 8, Day 2',
    subtitle: 'Риск, неопределённость и целые числа / Risk, Uncertainty and Signed Numbers',
    pages: [
      { title: 'Маршрут дня: пять слоёв обучения / Daily route: five learning layers', blocks: [
        { type: 'list', items: [
          '1. Математика / Math — 30–35 мин / min.',
          '2. Русский язык / Russian — 15–20 мин / min.',
          '3. Чтение и письмо / Reading and writing — 10–15 мин / min.',
          '4. Наблюдение и логика: матрица риска / Observation and logic: risk matrix — 5–10 мин / min.',
          '5. Практика: карточка риска / Practical: risk card — 10–15 мин / min.',
          '6. Дневник мышления / Thinking diary — 5 мин / min.',
          '7. Награда нимами / Reward in nims.'
        ] }
      ] },
      { title: 'Слой 1. Математика / Layer 1. Math', blocks: [
        { type: 'text', text: 'Grade 6: проценты, знаковые числа, уравнение со скобками. Показывай шаги. / Show steps.' },
        { type: 'list', items: [
          '1) 15% × 120 = ____ (высокий). 35% × 120 = ____ (средний). Низкий: 120 − ____ − ____ = ____. / Category split.',
          '2) 12 − 8 + 5 − 11 + 7 = ____ (сумма). Среднее ____ ÷ 5 = ____. Максимум ____. Минимум ____. Размах 12 − (−11) = ____. / Signed stats.',
          '3) 9 ÷ 45 = ____ = ____% (частота). Ожидание при 150 = 0.20 × 150 = ____. / Sample rate.',
          '4) MATH 4 — Уравнение проверки: 3x + 2(x + 4) = 58. 3x + 2x + 8 = ____. 5x = ____. x = ____. / Equation.',
          '5) Проверка / Check: 18 + 42 + 60 = ____ = 120? / Verify.',
          '6) Если 20% сохранится в 300 попытках, ожидание = ____ проблем. / Scale.',
          '7) Optional / Доп.: если высокий риск снизят до 10%, категория «высокий» станет = ____ решений. / What-if.',
          '8) Optional / Доп.: сумма положительных: 12 + 5 + 7 = ____; отрицательных: −8 − 11 = ____. Проверка: ____ + ____ = ____. / Sub-sums.'
        ] }
      ] },
      { title: 'Слой 2. Русский язык и правописание / Layer 2. Russian Language and Spelling', blocks: [
        { type: 'text', text: 'Правило / Rule: −ться (что делать?) / −тся (что делает?). Безударные проверяем ударением. / -tsya rule.' },
        { type: 'list', items: [
          'Словарь / Vocabulary: риск, вероятность, последствие, решение, проверка, отклонение.',
          '−тся/−ться: учит(ся), учиться, ошибиться, решает(ся), меняет(ся), проверять(ся) — определи форму каждого.',
          'Исправь ошибки / Fix mistakes: «риск не означаит абсолютной неудачи, а показываит возможныи последствия».',
          'Вставь букву / Insert vowel: р_шение, пров_рка, вер_ятность, посл_дствие.',
          'Мини-диктант / Mini-dictation: «Риск не означает абсолютной неудачи, а показывает возможные последствия».'
        ] }
      ] },
      { title: 'Слой 3. Чтение, письмо и пересказ / Layer 3. Reading, Writing and Retelling', blocks: [
        { type: 'list', items: [
          'Текст «Что такое риск» / Text: Риск — это не приговор. Он показывает, что результат может отличаться от плана: сильнее, слабее или совсем в другую сторону. У риска два показателя: вероятность события и его последствие. Если событие маловероятно, но последствие большое, ждать его всё равно нужно. Если вероятность высока, но последствие маленькое, оно вряд ли поменяет план. Хорошее решение учитывает обе стороны сразу. / Risk is not a verdict. It shows that the result can differ from the plan. Risk has two axes: probability and consequence. A good decision looks at both.',
          'Главная мысль / Main idea: __________________________________',
          'План из 3 пунктов / 3-point plan: 1) ______ 2) ______ 3) ______',
          '4 своих предложения о риске / 4 sentences: ______________________________',
          'Пересказ родителю 3–5 предложений по-русски / Retell in 3–5 Russian sentences ✓.'
        ] }
      ] },
      { title: 'Слой 4. Наблюдение и логика: матрица риска / Layer 4. Observation and Logic: Risk Matrix', blocks: [
        { type: 'text', text: 'Готовая таблица / Ready table:' },
        { type: 'list', items: [
          'Решение | Вероятность | Последствие',
          'A | низкая | большое',
          'B | высокая | маленькое',
          'C | средняя | среднее',
          'Какое нельзя оценивать только по вероятности? / Only by probability? — ____.',
          'Какое нельзя оценивать только по последствию? / Only by consequence? — ____.',
          'Какие два показателя нужны вместе? / Both axes together? — ____.',
          'Мой вывод / Conclusion: правильное решение учитывает и вероятность, и размер последствия. / Consider both.'
        ] }
      ] },
      { title: 'Слой 5. Практические знания: карточка риска / Layer 5. Practical Knowledge: risk card', blocks: [
        { type: 'text', text: 'Одно учебное решение. Не оценивай реальные покупки, здоровье или деньги. / Learning decision only.' },
        { type: 'list', items: [
          'Решение / Decision: __________________________________',
          'Вероятность / Probability (низкая/средняя/высокая): ____.',
          'Последствие / Consequence (маленькое/среднее/большое): ____.',
          'Что нужно проверить / What to check first: __________________________________',
          'Мой уровень уверенности 1–10 / Confidence 1–10: ____.',
          'Вывод / Conclusion: __________________________________'
        ] }
      ] },
      { title: 'Дневник мышления / Thinking Diary', blocks: [
        { type: 'list', items: [
          'Что я понял про риск / What I learned about risk: ______',
          'Самое важное слово / Most important word: ______'
        ] }
      ] },
      { title: 'Родительская проверка и награда нимами / Parent check and reward in nims', blocks: [
        { type: 'list', items: [
          'Базовая награда / Base reward: 3 нима / nims ✓',
          'Бонус за знаковые числа и уравнение / Bonus for signed numbers + equation: +1 ☐',
          'Бонус за матрицу риска / Bonus for risk matrix: +1 ☐',
          'Итого / Total: ____ из / of 5 нимов / nims'
        ] }
      ] }
    ]
  },
  parentGuide: {
    goal:
      'Grade 6: 15/35/50% от 120 = 18/42/60; знаковая сумма +5, среднее +1, размах 23; частота 20%, при 150 = 30; уравнение 3x + 2(x+4) = 58 → x = 10. Русский Grade 3–4. Учебная модель риска. / Educational model.',
    mathGoal: 'q1: 18/42/60. q2: +5, +1, 12, 23. q3: 20%; 30. MATH 4: x = 10. / Listed with steps.',
    russianGoal: '−ться: учиться, ошибиться, проверять(ся)/меняться (по вопросу). −тся: учит(ся), решает(ся). / By question test.',
    readingWritingGoal: 'Главная мысль: у риска две оси — вероятность и последствие. План + 4 предложения. / Main idea + plan + 4 sentences.',
    practicalGoal: 'Карточка риска: одно решение с обеими осями. / Risk card with both axes.',
    check: [
      'Q1: 18 / 42 / 60.',
      'Q2: +5, +1, 12, 23.',
      'Q3: 20%; 30 ожидается.',
      'MATH 4: x = 10.',
      'Q4: −тся/−ться разобраны. / -tsya sorted.',
      'Q5: исправление чистое. / Correction clean.',
      'Q6: главная мысль + план + 4 предложения. / Main idea + plan + 4 sentences.',
      'Q7: обе оси учтены. / Both axes.',
      'Q8: карточка полная. / Full risk card.',
      'Q9: 3–5 предложений. / 3–5 sentences.'
    ],
    answerKey: [
      { taskId: 'q1', answer: 'Ответ: Высокий 18; средний 42; низкий 60 решений. Решение: 0.15 × 120 = 18; 0.35 × 120 = 42; 120 − 18 − 42 = 60. Подсказка родителю: Все проценты — от одного целого 120.' },
      { taskId: 'q2', answer: 'Ответ: Сумма +5; среднее +1; максимум +12; размах 23. Решение: 12 − 8 + 5 − 11 + 7 = 5; 5 ÷ 5 = 1; min = −11; range = 12 − (−11) = 23. Подсказка родителю: Размах между положительным и отрицательным считается через вычитание отрицательного числа.' },
      { taskId: 'q3', answer: 'Ответ: 20%; при 150 попытках ожидается 30 проблем. Решение: 9 ÷ 45 = 0.20 = 20%; 0.20 × 150 = 30. Подсказка родителю: Прогноз при условии сохранения той же частоты.' },
      { taskId: 'q4', answer: 'Ответ: −ться: учиться, ошибиться, проверять(ся) в инфинитиве. −тся: учит(ся), решает(ся), меняет(ся) в 3-м лице. Решение: Проверка вопросом: «что делать?» → −ться; «что делает?» → −тся. Подсказка родителю: В −тся мягкого знака нет; в −ться — есть.' },
      { taskId: 'q5', answer: 'Ответ: Риск не означает абсолютной неудачи, а показывает возможные последствия. Решение: Исправлены: «означаит → означает», «показываит → показывает», «возможныи → возможные». Подсказка родителю: Обратите внимание на окончания глаголов 3-го лица.' },
      { taskId: 'q6', answer: 'Ответ: Главная мысль: у риска две оси — вероятность и последствие. Решение: План: 1) риск ≠ приговор; 2) две оси; 3) хорошее решение учитывает обе. + 4 свои предложения. Подсказка родителю: Пересказ 3–5 предложений по-русски.' },
      { taskId: 'q7', answer: 'Ответ: A нельзя по вероятности одной — последствие большое. B нельзя по последствию одному — вероятность высокая. Нужны обе оси. Решение: Матрица риска = вероятность × последствие; обе оси обязательны. Подсказка родителю: Принять любую формулировку, где обе оси названы явно.' },
      { taskId: 'q8', answer: 'Ответ: Открытый ответ: одно решение + оценка вероятности + оценка последствия + список проверок. Решение: Ребёнок выбирает учебное решение и оценивает его по двум осям. Подсказка родителю: Не давать оценивать реальные покупки, здоровье или деньги.' },
      { taskId: 'q9', answer: 'Ответ: 3–5 предложений о том, что каждая ось теряет половину картины отдельно. Решение: Ожидаемое: пример с A и B из матрицы риска. Подсказка родителю: Принимать разные примеры, если логика правильная.' }
    ],
    logicRubric: 'Принимается любой ответ, где обе оси матрицы упомянуты явно. / Both axes must be present.',
    readingRubric: 'Пересказ 3–5 предложений; главная мысль о двух осях риска. / Retelling essentials.',
    parentNotes: [
      'Это учебная модель риска, а не финансовая рекомендация. / Educational model of risk.',
      'Знаковый размах = максимум − минимум (даже если минимум отрицательный). / Range with signs.',
      'Уравнение со скобками решается через раскрытие и подобные. / Combine like terms.'
    ],
    spellingCheck: '−тся/−ться правильно; безударные проверяемые правильные. / -tsya rule + vowels.',
    readingCheck: 'Главная мысль ясная; план 3 пункта; 4 предложения; пересказ 3–5 предложений. / Reading essentials.',
    notebookCheck: 'Все 5 слоёв заполнены. / All 5 layers.',
    printCheck: 'Все 8 страниц заполнены. / All 8 pages.',
    discussionPrompts: [
      'Какой риск в твоей неделе самый неожиданный? / Which risk in your week is most surprising?',
      'Когда высокая вероятность важнее большого последствия? / When probability beats consequence?',
      'Как ты снижаешь риск в задании? / How do you lower risk in a task?',
      'Какая проверка помогает больше всего? / Which check helps most?'
    ],
    ifEasy: 'Усложни: если высокий риск снизят до 10%, а средний — до 30%, пересчитай категории. / Extend.',
    ifDifficult: 'Оставь q1 и q3. / Keep q1 and q3.',
    tokenReward:
      'После проверки можно начислить бонусные нимы через Parent Room. Нимы — это призовые токены курса, а не оплата. / Nims are course prize tokens, not payment.'
  },
  reward: {
    unit: 'nim', unitName: 'ним', base: 3, max: 5,
    label: 'Награда за завершение / Completion reward', display: '3 нима / 3 nims',
    bonuses: [
      { id: 'signed-eq', label: 'Бонус за знаковые числа и уравнение / Bonus for signed numbers + equation', amount: 1 },
      { id: 'risk-matrix', label: 'Бонус за матрицу риска / Bonus for risk matrix', amount: 1 }
    ]
  },
  completionRequirements: { requiredTaskIds: ['q1', 'q4', 'q6', 'q9'], allowNotebookCompletion: true },
  unlockAfter: { weekId: 'week-8', dayId: 'day-1' },
  nextLessonId: 'max-week-8-day-3'
};

export default maxWeek8Day2;
